/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: hooks/useSecurityLogList.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    Hook for managing Security Logs list
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAuthStore } from '../../../store/auth';
import { fetchSecurityLogs } from '../api/securityLogApi';
import { SecurityLog, ListSecurityLogsParams, SecurityLogFilterValues } from '../types/securityLog.types';

/**
 * Hook for managing Security Logs list
 */
export function useSecurityLogList() {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';
  const userId = user?.id;

  // Filter state - Default to ALL actions to see what's available
  const [filters, setFilters] = useState<SecurityLogFilterValues>({
    startDate: '',
    endDate: '',
    actions: [],  // Empty = show all actions to see what's being logged
    resource: '',
    userId: '',
    domainId: '',
  });

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    try {
      if (!userId) {
        throw new Error('User ID not available');
      }

      const queryParams: ListSecurityLogsParams = {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        search: params.search,
        // Add filters to query params
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        action: filters.actions.length > 0 ? filters.actions.join(',') : undefined,
        resource: filters.resource || undefined,
        userId: filters.userId || undefined,
        domainId: filters.domainId ? parseInt(filters.domainId, 10) : undefined,
      };

      const result = await fetchSecurityLogs(queryParams, tenantSlug, userId);
      
      return {
        data: result.data,
        pagination: result.pagination
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('securityLogs.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, filters, tenantSlug, userId]);

  const availableColumns = useMemo(() => [
    { key: 'id', label: t('securityLogs.columns.id'), visible: false, sortable: false },
    { key: 'createdAt', label: t('securityLogs.columns.timestamp'), visible: true, sortable: true },
    { key: 'action', label: t('securityLogs.columns.action'), visible: true, sortable: true },
    { key: 'resource', label: t('securityLogs.columns.resource'), visible: true, sortable: true },
    { key: 'resourceId', label: t('securityLogs.columns.resourceId'), visible: false, sortable: false },
    { key: 'user', label: t('securityLogs.columns.user'), visible: true, sortable: false },
    { key: 'ipAddress', label: t('securityLogs.columns.ipAddress'), visible: true, sortable: false },
    { key: 'userAgent', label: t('securityLogs.columns.userAgent'), visible: false, sortable: false },
    { key: 'metadata', label: t('securityLogs.columns.metadata'), visible: true, sortable: false },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 50,
    moduleName: 'securityLogs'
  });

  // Filter handlers
  const handleFilterChange = useCallback((newFilters: SecurityLogFilterValues) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      actions: [],  // Empty = show all actions
      resource: '',
      userId: '',
      domainId: '',
    });
    setTimeout(() => refresh(), 100);
  }, [refresh]);

  const handleApplyFilters = useCallback(() => {
    refresh();
  }, [refresh]);

  return {
    data: data as SecurityLog[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
    refresh
  };
}

